import Button from '@jetbrains/ring-ui-built/components/button/button'
import Dropdown, {Anchor} from '@jetbrains/ring-ui-built/components/dropdown/dropdown'
import {H2} from '@jetbrains/ring-ui-built/components/heading/heading'
import {HTTPError, HTTPCodesEnum} from '@jetbrains/teamcity-ui/dist/services'
import {useEffect} from 'react'
import {useLocation, useRouteError} from 'react-router'

import EntityLink from '../../../containers/EntityLink'
import {
  useBooleanQueryParamState,
  useBuildId,
  useBuildTypeId,
  usePrevLocation,
  useQueryParams,
} from '../../../hooks/routes'
import {useDarkOrLightTheme} from '../../../hooks/theme/useDarkOrLightTheme'
import {extractErrorFromRelay} from '../../../relay/extractErrorFromRelay'
import {
  getAgentsOverviewHref,
  getAgentTypeHref,
  getFavoriteAgentPoolsHref,
  isAgentsScreen,
} from '../../../routes'
import {toAgentTypeId} from '../../../types'
import getErrorStatusCode from '../../../utils/getErrorStatusCode'
import FullScreenErrorWrapper from '../../pages/PipelinesPages/components/FullScreenErrorWrapper/FullScreenErrorWrapper'
import BuildTypeNamePlain from '../BuildTypeName/BuildTypeName'
import PageNotFound from '../PageNotFound/PageNotFound'
import Popup from '../Popup/Popup.lazy'
import RouterLink, {RouterButton} from '../RouterLink/RouterLink'

import errorDark from './Error_dark.svg'
import errorLight from './Error_light.svg'

import styles from './ErrorPage.module.css'

const reload = () => window.location.reload()

export default function ErrorPage() {
  const queryParams = useQueryParams()
  const buildTypeId = useBuildTypeId()
  const buildId = useBuildId()
  const location = useLocation()
  const prevLocation = usePrevLocation()
  const routeError = useRouteError() as Error | null | undefined
  const error = routeError && extractErrorFromRelay(routeError)
  const statusCode = getErrorStatusCode(error)
  const initialErrorMessage = error?.message ?? ''
  const isNotFound = statusCode === HTTPCodesEnum.NOT_FOUND
  const errorMessage =
    isNotFound && initialErrorMessage === '' ? 'Page not found' : initialErrorMessage
  const isHtml = /^</.test(errorMessage.trim())
  const [isReloaded, setIsReloaded] = useBooleanQueryParamState('isReloaded')

  if (process.env.NODE_ENV !== 'test') {
    // eslint-disable-next-line no-console
    console.error(errorMessage)
  }

  useEffect(() => {
    if (error?.name === 'ChunkLoadError' && !isReloaded) {
      setIsReloaded(true)
      window.location.reload()
    }
  }, [error?.name, isReloaded, setIsReloaded])
  const theme = useDarkOrLightTheme()
  const isDark = theme === 'dark'

  const referrer = IS_STORYBOOK ? '/' : document.referrer

  if (isNotFound) {
    return (
      <FullScreenErrorWrapper>
        <PageNotFound message={initialErrorMessage} />
      </FullScreenErrorWrapper>
    )
  }

  return (
    <div className={styles.errorPage}>
      <img
        alt="Error"
        src={isDark ? errorDark : errorLight}
        width={280}
        height={200}
        title="Error"
      />
      <div className={styles.text}>
        {statusCode != null && <H2 className={styles.code}>{statusCode}</H2>}
        {isHtml ? (
          <H2 className={styles.title}>
            <Dropdown
              hoverMode
              anchor={<Anchor className={styles.details}>{'Network error'}</Anchor>}
            >
              <Popup className={styles.detailsPopup}>
                {error instanceof HTTPError ? (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: errorMessage,
                    }}
                  />
                ) : (
                  errorMessage
                )}
              </Popup>
            </Dropdown>
          </H2>
        ) : error instanceof HTTPError ? (
          <div className={styles.title} />
        ) : (
          <H2 className={styles.title}>{'Something went wrong'}</H2>
        )}
        {!isHtml && <div className={styles.description}>{errorMessage}</div>}
        <div className={styles.actions}>
          <Button primary onClick={reload}>
            {'Refresh page'}
          </Button>
          {isAgentsScreen(location.pathname) && (
            <>
              <RouterLink to={getAgentsOverviewHref()}>{'Agents overview'}</RouterLink>
              <br />
              <RouterLink to={getFavoriteAgentPoolsHref()}>{'Favorite agents pools'}</RouterLink>
              <br />
              {queryParams.agentTypeId && (
                <>
                  <RouterLink to={getAgentTypeHref(toAgentTypeId(queryParams.agentTypeId))}>
                    {'Cloud image'}
                  </RouterLink>
                  <br />
                </>
              )}
            </>
          )}
          {buildId != null && buildTypeId != null && (
            <>
              <EntityLink buildTypeId={buildTypeId}>
                <BuildTypeNamePlain buildTypeId={buildTypeId} />
              </EntityLink>
            </>
          )}
          {prevLocation ? (
            <RouterButton to={`${prevLocation.pathname}${prevLocation.search}`}>
              {'Go back'}
            </RouterButton>
          ) : (
            referrer && <Button href={referrer}>{'Go back'}</Button>
          )}
        </div>
      </div>
    </div>
  )
}
